import React, { useState } from 'react';
import Icon from '../AppIcon';

/**
 * Reusable FAQ Accordion Component
 * 
 * Features:
 * - ARIA accessible
 * - SEO-friendly (content always in HTML)
 * - Server-side render friendly
 * - Keyboard navigation support
 * 
 * @param {Array} items - Array of FAQ items with { question, answer }
 * @param {string} className - Additional CSS classes
 * @param {boolean} allowMultiple - Allow multiple items open at once
 * @param {string} variant - Visual variant ('default', 'bordered', 'minimal')
 */
const FAQ = ({ 
  items = [], 
  className = '',
  allowMultiple = false,
  variant = 'default'
}) => {
  const [openItems, setOpenItems] = useState(new Set());

  const toggleItem = (index) => {
    setOpenItems(prev => {
      const newSet = new Set(prev);
      
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        if (!allowMultiple) {
          newSet.clear();
        }
        newSet.add(index);
      }
      
      return newSet;
    });
  };

  const isOpen = (index) => openItems.has(index);

  // Variant styles
  const variantStyles = {
    default: {
      container: 'space-y-4',
      item: 'bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow',
      question: 'text-gray-900',
      answer: 'text-gray-600'
    },
    bordered: {
      container: 'space-y-3',
      item: 'border-b border-gray-200 pb-4',
      question: 'text-gray-900',
      answer: 'text-gray-600'
    },
    minimal: {
      container: 'space-y-2',
      item: 'bg-transparent',
      question: 'text-gray-900',
      answer: 'text-gray-600'
    }
  };

  const styles = variantStyles[variant] || variantStyles.default;

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div className={`faq-container ${styles.container} ${className}`}>
      {items.map((item, index) => {
        const itemId = `faq-item-${index}`;
        const questionId = `faq-question-${index}`;
        const answerId = `faq-answer-${index}`;
        const isItemOpen = isOpen(index);

        return (
          <div
            key={index}
            className={`faq-item ${styles.item}`}
            itemScope
            itemType="https://schema.org/Question"
          >
            <button
              type="button"
              className="w-full text-left focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-2 rounded-lg"
              onClick={() => toggleItem(index)}
              aria-expanded={isItemOpen}
              aria-controls={answerId}
              id={questionId}
            >
              <div className="flex items-center justify-between p-4 md:p-6">
                <h3
                  className={`text-base md:text-lg font-semibold pr-8 ${styles.question}`}
                  itemProp="name"
                >
                  {item.question}
                </h3>
                <div className="flex-shrink-0">
                  <Icon
                    name="ChevronDown"
                    size={20}
                    className={`text-gray-500 transition-transform duration-200 ${
                      isItemOpen ? 'transform rotate-180' : ''
                    }`}
                    aria-hidden="true"
                  />
                </div>
              </div>
            </button>
            
            <div
              id={answerId}
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                isItemOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
              }`}
              aria-hidden={!isItemOpen}
              role="region"
              aria-labelledby={questionId}
            >
              <div
                className={`px-4 md:px-6 pb-4 md:pb-6 ${styles.answer}`}
                itemScope
                itemType="https://schema.org/Answer"
              >
                <div itemProp="text" className="prose prose-sm max-w-none">
                  {typeof item.answer === 'string' ? (
                    <p className="leading-relaxed">{item.answer}</p>
                  ) : (
                    <div className="leading-relaxed">{item.answer}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FAQ;
